import { useTranslation } from "react-i18next";
import {
  DoubleMaterialityESRSAreaEnum,
  DoubleMaterialityStakeholderESRSArea,
} from "@green0meter/api-client";

import type { DoubleMaterialityESRSArea } from "../../../Stakeholders/components/ESRS.constants";
import { Subtopic, SubSubTopic } from "./AssessmentEditor.utils";
import {
  ESRS_ENUM_TO_STRING,
  ESRS_STRING_TO_ENUM,
  ESRS_TYPES,
} from "../../../Stakeholders/components/ESRS.constants";

type ESRSTopicConfig = {
  name: string;
  subtopics: Subtopic[];
};

export const useESRSTopics = (): {
  ESRS_TOPICS: typeof ESRS_TOPICS;
  TOPIC_OPTIONS: Array<{ code: DoubleMaterialityESRSAreaEnum; name: string }>;
  getSubtopicsForTopic: (
    topicCode: string | number | null | undefined,
  ) => Subtopic[];
  getSubSubtopicsForTopic: (
    topicCode: string | number | null | undefined,
    subTopicCode: string,
  ) => SubSubTopic[];
} => {
  const { t } = useTranslation();

  //  Topics and Subtopics structure
  const ESRS_TOPICS: Record<string, ESRSTopicConfig> = {
    E1: {
      name: t("dma.e1.climate_change", "Climate Change"),
      subtopics: [
        {
          code: "climate_change_adaptation",
          name: t(
            "dma.e1.subtopic_climate_change_adaptation",
            "Climate Change Adaptation",
          ),
        },
        {
          code: "climate_change_mitigation",
          name: t(
            "dma.e1.subtopic_climate_change_mitigation",
            "Climate Change Mitigation",
          ),
        },
        {
          code: "energy",
          name: t("dma.e1.subtopic_energy", "Energy"),
        },
      ],
    },
    E2: {
      name: t("dma.e2.pollution", "Pollution"),
      subtopics: [
        {
          code: "pollution_of_air",
          name: t("dma.e2.subtopic_pollution_of_air", "Pollution of Air"),
        },
        {
          code: "pollution_of_water",
          name: t("dma.e2.subtopic_pollution_of_water", "Pollution of Water"),
        },
        {
          code: "pollution_of_soil",
          name: t("dma.e2.subtopic_pollution_of_soil", "Pollution of Soil"),
        },
        {
          code: "pollution_of_living_organisms_and_food_resources",
          name: t(
            "dma.e2.subtopic_pollution_of_living_organisms_and_food_resources",
            "Pollution of Living Organisms and Food Resources",
          ),
        },
        {
          code: "substances_of_concern",
          name: t("dma.e2.subtopic_substances_of_concern", "Substances of Concern"),
        },
        {
          code: "substances_of_very_high_concern",
          name: t(
            "dma.e2.subtopic_substances_of_very_high_concern",
            "Substances of Very High Concern",
          ),
        },
        {
          code: "microplastics",
          name: t("dma.e2.subtopic_microplastics", "Microplastics"),
        },
      ],
    },
    E3: {
      name: t("dma.e3.water_and_marine_resources", "Water and Marine Resources"),
      subtopics: [
        {
          code: "water",
          name: t("dma.e3.subtopic_water", "Water"),
          subSubTopics: [
            {
              code: "water_consumption",
              name: t("dma.e3.subsubtopic_water_consumption", "Water Consumption"),
            },
            {
              code: "water_withdrawals",
              name: t("dma.e3.subsubtopic_water_withdrawals", "Water Withdrawals"),
            },
            {
              code: "water_discharges",
              name: t("dma.e3.subsubtopic_water_discharges", "Water Discharges"),
            },
            {
              code: "water_discharges_in_the_oceans",
              name: t(
                "dma.e3.subsubtopic_water_discharges_in_the_oceans",
                "Water Discharges in the Oceans",
              ),
            },
          ],
        },
        {
          code: "marine_resources",
          name: t("dma.e3.subtopic_marine_resources", "Marine Resources"),
          subSubTopics: [
            {
              code: "extraction_and_use_of_marine_resources",
              name: t(
                "dma.e3.subsubtopic_extraction_and_use_of_marine_resources",
                "Extraction and Use of Marine Resources",
              ),
            },
          ],
        },
      ],
    },
    E4: {
      name: t("dma.e4.biodiversity_and_ecosystems", "Biodiversity and Ecosystems"),
      subtopics: [
        {
          code: "direct_impact_drivers_of_biodiversity_loss",
          name: t(
            "dma.e4.subtopic_direct_impact_drivers_of_biodiversity_loss",
            "Direct Impact Drivers of Biodiversity Loss",
          ),
          subSubTopics: [
            {
              code: "climate_change",
              name: t("dma.e4.subsubtopic_climate_change", "Climate Change"),
            },
            {
              code: "land_use_change_fresh_water_use_change_and_sea_use_change",
              name: t(
                "dma.e4.subsubtopic_land_use_change",
                "Land-use change, fresh water-use change and sea-use change",
              ),
            },
            {
              code: "direct_exploitation",
              name: t(
                "dma.e4.subsubtopic_direct_exploitation",
                "Direct Exploitation",
              ),
            },
            {
              code: "invasive_alien_species",
              name: t(
                "dma.e4.subsubtopic_invasive_alien_species",
                "Invasive Alien Species",
              ),
            },
            {
              code: "pollution",
              name: t("dma.e4.subsubtopic_pollution", "Pollution"),
            },
            {
              code: "others",
              name: t("dma.e4.subsubtopic_others", "Others"),
            },
          ],
        },
        {
          code: "impacts_on_the_state_of_species",
          name: t(
            "dma.e4.subtopic_impacts_on_the_state_of_species",
            "Impacts on the State of Species",
          ),
          subSubTopics: [
            {
              code: "species_population_size",
              name: t(
                "dma.e4.subsubtopic_species_population_size",
                "Species Population Size",
              ),
            },
            {
              code: "species_global_extinction_risk",
              name: t(
                "dma.e4.subsubtopic_species_global_extinction_risk",
                "Species Global Extinction Risk",
              ),
            },
          ],
        },
        {
          code: "impacts_on_the_extent_and_condition_of_ecosystems",
          name: t(
            "dma.e4.subtopic_impacts_on_the_extent_and_condition_of_ecosystems",
            "Impacts on the Extent and Condition of Ecosystems",
          ),
          subSubTopics: [
            {
              code: "land_degradation",
              name: t("dma.e4.subsubtopic_land_degradation", "Land Degradation"),
            },
            {
              code: "desertification",
              name: t("dma.e4.subsubtopic_desertification", "Desertification"),
            },
            {
              code: "soil_sealing",
              name: t("dma.e4.subsubtopic_soil_sealing", "Soil Sealing"),
            },
          ],
        },
        {
          code: "impacts_and_dependencies_on_ecosystem_services",
          name: t(
            "dma.e4.subtopic_impacts_and_dependencies_on_ecosystem_services",
            "Impacts and Dependencies on Ecosystem Services",
          ),
        },
      ],
    },
    E5: {
      name: t("dma.e5.circular_economy", "Circular Economy"),
      subtopics: [
        {
          code: "resource_inflows_including_resource_use",
          name: t(
            "dma.e5.subtopic_resource_inflows_including_resource_use",
            "Resource Inflows, Including Resource Use",
          ),
        },
        {
          code: "resource_outflows_related_to_products_and_services",
          name: t(
            "dma.e5.subtopic_resource_outflows_related_to_products_and_services",
            "Resource Outflows Related to Products and Services",
          ),
        },
        {
          code: "waste",
          name: t("dma.e5.subtopic_waste", "Waste"),
        },
      ],
    },
    S1: {
      name: t("dma.s1.own_workforce", "Own Workforce"),
      subtopics: [
        {
          code: "working_conditions",
          name: t("dma.s1.subtopic_working_conditions", "Working Conditions"),
          subSubTopics: [
            {
              code: "secure_employment",
              name: t(
                "dma.s1.subsubtopic_working_conditions_secure_employment",
                "Secure Employment",
              ),
            },
            {
              code: "working_time",
              name: t(
                "dma.s1.subsubtopic_working_conditions_working_time",
                "Working Time",
              ),
            },
            {
              code: "adequate_wages",
              name: t(
                "dma.s1.subsubtopic_working_conditions_adequate_wages",
                "Adequate Wages",
              ),
            },
            {
              code: "social_dialogue",
              name: t(
                "dma.s1.subsubtopic_working_conditions_social_dialogue",
                "Social Dialogue",
              ),
            },
            {
              code: "freedom_of_association_information_consultation_participation_rights",
              name: t(
                "dma.s1.subsubtopic_working_conditions_freedom_of_association_information_consultation_participation_rights",
                "Freedom of Association, the Existence of Works Councils and the Information, Consultation and Participation Rights of Workers",
              ),
            },
            {
              code: "collective_bargaining_workers_covered_rate",
              name: t(
                "dma.s1.subsubtopic_working_conditions_collective_bargaining_workers_covered_rate",
                "Collective Bargaining, Including Rate of Workers Covered by Collective Agreements",
              ),
            },
            {
              code: "work_life_balance",
              name: t(
                "dma.s1.subsubtopic_working_conditions_work_life_balance",
                "Work-Life Balance",
              ),
            },
            {
              code: "health_and_safety",
              name: t(
                "dma.s1.subsubtopic_working_conditions_health_and_safety",
                "Health and Safety",
              ),
            },
          ],
        },
        {
          code: "equal_treatment_and_opportunities_for_all",
          name: t(
            "dma.s1.subtopic_equal_treatment_and_opportunities_for_all",
            "Equal Treatment and Opportunities for All",
          ),
          subSubTopics: [
            {
              code: "gender_equality_equal_pay",
              name: t(
                "dma.s1.subsubtopic_equal_treatment_and_opportunities_for_all_gender_equality_equal_pay",
                "Gender Equality and Equal Pay for Work of Equal Value",
              ),
            },
            {
              code: "training_and_skills_development",
              name: t(
                "dma.s1.subsubtopic_equal_treatment_and_opportunities_for_all_training_and_skills_development",
                "Training and Skills Development",
              ),
            },
            {
              code: "employment_and_inclusion_of_persons_with_disabilities",
              name: t(
                "dma.s1.subsubtopic_equal_treatment_and_opportunities_for_all_employment_and_inclusion_of_persons_with_disabilities",
                "Employment and Inclusion of Persons with Disabilities",
              ),
            },
            {
              code: "measures_against_violence_and_harassment",
              name: t(
                "dma.s1.subsubtopic_equal_treatment_and_opportunities_for_all_measures_against_violence_and_harassment",
                "Measures Against Violence and Harassment in the Workplace",
              ),
            },
            {
              code: "diversity",
              name: t(
                "dma.s1.subsubtopic_equal_treatment_and_opportunities_for_all_diversity",
                "Diversity",
              ),
            },
          ],
        },
        {
          code: "other_work_related_rights",
          name: t(
            "dma.s1.subtopic_other_work_related_rights",
            "Other Work-Related Rights",
          ),
          subSubTopics: [
            {
              code: "child_labour",
              name: t(
                "dma.s1.subsubtopic_other_work_related_rights_child_labour",
                "Child Labour",
              ),
            },
            {
              code: "forced_labour",
              name: t(
                "dma.s1.subsubtopic_other_work_related_rights_forced_labour",
                "Forced Labour",
              ),
            },
            {
              code: "adequate_housing",
              name: t(
                "dma.s1.subsubtopic_other_work_related_rights_adequate_housing",
                "Adequate Housing",
              ),
            },
            {
              code: "privacy",
              name: t(
                "dma.s1.subsubtopic_other_work_related_rights_privacy",
                "Privacy",
              ),
            },
          ],
        },
      ],
    },
    S2: {
      name: t("dma.s2.workers_in_the_value_chain", "Workers in the Value Chain"),
      subtopics: [
        {
          code: "working_conditions",
          name: t("dma.s2.subtopic_working_conditions", "Working Conditions"),
          subSubTopics: [
            {
              code: "secure_employment",
              name: t(
                "dma.s2.subsubtopic_working_conditions_secure_employment",
                "Secure Employment",
              ),
            },
            {
              code: "working_time",
              name: t(
                "dma.s2.subsubtopic_working_conditions_working_time",
                "Working Time",
              ),
            },
            {
              code: "adequate_wages",
              name: t(
                "dma.s2.subsubtopic_working_conditions_adequate_wages",
                "Adequate Wages",
              ),
            },
            {
              code: "social_dialogue",
              name: t(
                "dma.s2.subsubtopic_working_conditions_social_dialogue",
                "Social Dialogue",
              ),
            },
            {
              code: "freedom_of_association_works_councils",
              name: t(
                "dma.s2.subsubtopic_working_conditions_freedom_of_association_works_councils",
                "Freedom of Association, Including the Existence of Works Councils",
              ),
            },
            {
              code: "collective_bargaining",
              name: t(
                "dma.s2.subsubtopic_working_conditions_collective_bargaining",
                "Collective Bargaining",
              ),
            },
            {
              code: "work_life_balance",
              name: t(
                "dma.s2.subsubtopic_working_conditions_work_life_balance",
                "Work-Life Balance",
              ),
            },
            {
              code: "health_and_safety",
              name: t(
                "dma.s2.subsubtopic_working_conditions_health_and_safety",
                "Health and Safety",
              ),
            },
          ],
        },
        {
          code: "equal_treatment_and_opportunities_for_all",
          name: t(
            "dma.s2.subtopic_equal_treatment_and_opportunities_for_all",
            "Equal Treatment and Opportunities for All",
          ),
          subSubTopics: [
            {
              code: "gender_equality_equal_pay",
              name: t(
                "dma.s2.subsubtopic_equal_treatment_and_opportunities_for_all_gender_equality_equal_pay",
                "Gender Equality and Equal Pay for Work of Equal Value",
              ),
            },
            {
              code: "training_and_skills_development",
              name: t(
                "dma.s2.subsubtopic_equal_treatment_and_opportunities_for_all_training_and_skills_development",
                "Training and Skills Development",
              ),
            },
            {
              code: "employment_and_inclusion_of_persons_with_disabilities",
              name: t(
                "dma.s2.subsubtopic_equal_treatment_and_opportunities_for_all_employment_and_inclusion_of_persons_with_disabilities",
                "The Employment and Inclusion of Persons with Disabilities",
              ),
            },
            {
              code: "measures_against_violence_and_harassment",
              name: t(
                "dma.s2.subsubtopic_equal_treatment_and_opportunities_for_all_measures_against_violence_and_harassment",
                "Measures Against Violence and Harassment in the Workplace",
              ),
            },
            {
              code: "diversity",
              name: t(
                "dma.s2.subsubtopic_equal_treatment_and_opportunities_for_all_diversity",
                "Diversity",
              ),
            },
          ],
        },
        {
          code: "other_work_related_rights",
          name: t(
            "dma.s2.subtopic_other_work_related_rights",
            "Other Work-Related Rights",
          ),
          subSubTopics: [
            {
              code: "child_labour",
              name: t(
                "dma.s2.subsubtopic_other_work_related_rights_child_labour",
                "Child Labour",
              ),
            },
            {
              code: "forced_labour",
              name: t(
                "dma.s2.subsubtopic_other_work_related_rights_forced_labour",
                "Forced Labour",
              ),
            },
            {
              code: "adequate_housing",
              name: t(
                "dma.s2.subsubtopic_other_work_related_rights_adequate_housing",
                "Adequate Housing",
              ),
            },
            {
              code: "water_and_sanitation",
              name: t(
                "dma.s2.subsubtopic_other_work_related_rights_water_and_sanitation",
                "Water and Sanitation",
              ),
            },
            {
              code: "privacy",
              name: t(
                "dma.s2.subsubtopic_other_work_related_rights_privacy",
                "Privacy",
              ),
            },
          ],
        },
      ],
    },
    S3: {
      name: t("dma.s3.affected_communities", "Affected Communities"),
      subtopics: [
        {
          code: "communities_economic_social_and_cultural_rights",
          name: t(
            "dma.s3.subtopic_communities_economic_social_and_cultural_rights",
            "Communities' Economic, Social and Cultural Rights",
          ),
          subSubTopics: [
            {
              code: "adequate_housing",
              name: t(
                "dma.s3.subsubtopic_communities_economic_social_and_cultural_rights_adequate_housing",
                "Adequate Housing",
              ),
            },
            {
              code: "adequate_food",
              name: t(
                "dma.s3.subsubtopic_communities_economic_social_and_cultural_rights_adequate_food",
                "Adequate Food",
              ),
            },
            {
              code: "water_and_sanitation",
              name: t(
                "dma.s3.subsubtopic_communities_economic_social_and_cultural_rights_water_and_sanitation",
                "Water and Sanitation",
              ),
            },
            {
              code: "land_related_impacts",
              name: t(
                "dma.s3.subsubtopic_communities_economic_social_and_cultural_rights_land_related_impacts",
                "Land-Related Impacts",
              ),
            },
            {
              code: "security_related_impacts",
              name: t(
                "dma.s3.subsubtopic_communities_economic_social_and_cultural_rights_security_related_impacts",
                "Security-Related Impacts",
              ),
            },
          ],
        },
        {
          code: "communities_civil_and_political_rights",
          name: t(
            "dma.s3.subtopic_communities_civil_and_political_rights",
            "Communities' Civil and Political Rights",
          ),
          subSubTopics: [
            {
              code: "freedom_of_expression",
              name: t(
                "dma.s3.subsubtopic_communities_civil_and_political_rights_freedom_of_expression",
                "Freedom of Expression",
              ),
            },
            {
              code: "freedom_of_assembly",
              name: t(
                "dma.s3.subsubtopic_communities_civil_and_political_rights_freedom_of_assembly",
                "Freedom of Assembly",
              ),
            },
            {
              code: "impacts_on_human_rights_defenders",
              name: t(
                "dma.s3.subsubtopic_communities_civil_and_political_rights_impacts_on_human_rights_defenders",
                "Impacts on Human Rights Defenders",
              ),
            },
          ],
        },
        {
          code: "rights_of_indigenous_peoples",
          name: t(
            "dma.s3.subtopic_rights_of_indigenous_peoples",
            "Rights of Indigenous Peoples",
          ),
          subSubTopics: [
            {
              code: "free_prior_informed_consent",
              name: t(
                "dma.s3.subsubtopic_rights_of_indigenous_peoples_free_prior_informed_consent",
                "Free, Prior and Informed Consent",
              ),
            },
            {
              code: "self_determination",
              name: t(
                "dma.s3.subsubtopic_rights_of_indigenous_peoples_self_determination",
                "Self-Determination",
              ),
            },
            {
              code: "cultural_rights",
              name: t(
                "dma.s3.subsubtopic_rights_of_indigenous_peoples_cultural_rights",
                "Cultural Rights",
              ),
            },
          ],
        },
      ],
    },
    S4: {
      name: t("dma.s4.consumers_and_end_users", "Consumers and End-users"),
      subtopics: [
        {
          code: "information_related_impacts",
          name: t(
            "dma.s4.subtopic_information_related_impacts",
            "Information-Related Impacts for Consumers and/or End-users",
          ),
          subSubTopics: [
            {
              code: "privacy",
              name: t(
                "dma.s4.subsubtopic_information_related_impacts_privacy",
                "Privacy",
              ),
            },
            {
              code: "freedom_of_expression",
              name: t(
                "dma.s4.subsubtopic_information_related_impacts_freedom_of_expression",
                "Freedom of Expression",
              ),
            },
            {
              code: "access_to_quality_information",
              name: t(
                "dma.s4.subsubtopic_information_related_impacts_access_to_quality_information",
                "Access to (Quality) Information",
              ),
            },
          ],
        },
        {
          code: "personal_safety",
          name: t(
            "dma.s4.subtopic_personal_safety",
            "Personal Safety of Consumers and/or End-users",
          ),
          subSubTopics: [
            {
              code: "health_and_safety",
              name: t(
                "dma.s4.subsubtopic_personal_safety_health_and_safety",
                "Health and Safety",
              ),
            },
            {
              code: "security_of_a_person",
              name: t(
                "dma.s4.subsubtopic_personal_safety_security_of_a_person",
                "Security of a Person",
              ),
            },
            {
              code: "protection_of_children",
              name: t(
                "dma.s4.subsubtopic_personal_safety_protection_of_children",
                "Protection of Children",
              ),
            },
          ],
        },
        {
          code: "social_inclusion",
          name: t(
            "dma.s4.subtopic_social_inclusion",
            "Social Inclusion of Consumers and/or End-users",
          ),
          subSubTopics: [
            {
              code: "non_discrimination",
              name: t(
                "dma.s4.subsubtopic_social_inclusion_non_discrimination",
                "Non-discrimination",
              ),
            },
            {
              code: "access_to_products_and_services",
              name: t(
                "dma.s4.subsubtopic_social_inclusion_access_to_products_and_services",
                "Access to Products and Services",
              ),
            },
            {
              code: "responsible_marketing_practices",
              name: t(
                "dma.s4.subsubtopic_social_inclusion_responsible_marketing_practices",
                "Responsible Marketing Practices",
              ),
            },
          ],
        },
      ],
    },
    G1: {
      name: t("dma.g1.business_conduct", "Business Conduct"),
      subtopics: [
        {
          code: "corporate_culture",
          name: t("dma.g1.subtopic_corporate_culture", "Corporate Culture"),
        },
        {
          code: "protection_of_whistle_blowers",
          name: t(
            "dma.g1.subtopic_protection_of_whistle_blowers",
            "Protection of Whistle-blowers",
          ),
        },
        {
          code: "animal_welfare",
          name: t("dma.g1.subtopic_animal_welfare", "Animal Welfare"),
        },
        {
          code: "political_engagement",
          name: t(
            "dma.g1.subtopic_political_engagement",
            "Political Engagement and Lobbying Activities",
          ),
        },
        {
          code: "management_of_relationships_with_suppliers",
          name: t(
            "dma.g1.subtopic_management_of_relationships_with_suppliers",
            "Management of Relationships with Suppliers Including Payment Practices",
          ),
        },
        {
          code: "corruption_and_bribery",
          name: t(
            "dma.g1.subtopic_corruption_and_bribery",
            "Corruption and Bribery",
          ),
          subSubTopics: [
            {
              code: "prevention_and_detection",
              name: t(
                "dma.g1.subsubtopic_corruption_and_bribery_prevention_and_detection",
                "Prevention and Detection Including Training",
              ),
            },
            {
              code: "incidents",
              name: t(
                "dma.g1.subsubtopic_corruption_and_bribery_incidents",
                "Incidents",
              ),
            },
          ],
        },
      ],
    },
    OTHER: {
      name: t("dma.other.other", "Other"),
      subtopics: [],
    },
  };

  // Extract topic names for the dropdown
  const TOPIC_OPTIONS = Object.entries(ESRS_TOPICS).map(([code, topic]) => ({
    code: ESRS_STRING_TO_ENUM[code as DoubleMaterialityESRSArea],
    name: code === "OTHER" ? topic.name : `${code}: ${topic.name}`,
  }));

  const resolveTopicKey = (
    topicCode: string | number | null | undefined,
  ): string | null => {
    if (topicCode === null || topicCode === undefined) {
      return null;
    }

    if (typeof topicCode === "number") {
      return ESRS_ENUM_TO_STRING[
        topicCode as DoubleMaterialityESRSAreaEnum
      ] as string;
    }

    if (
      Object.values(DoubleMaterialityStakeholderESRSArea).includes(
        topicCode as DoubleMaterialityStakeholderESRSArea,
      )
    ) {
      return ESRS_TYPES[topicCode as DoubleMaterialityStakeholderESRSArea];
    }

    return topicCode;
  };

  // Get subtopics for a given topic code
  const getSubtopicsForTopic = (
    topicCode: string | number | null | undefined,
  ): Subtopic[] => {
    const topicKey = resolveTopicKey(topicCode);

    if (!topicKey) return [];
    const topic = ESRS_TOPICS[topicKey];

    return topic
      ? topic.subtopics.map((subtopic) => ({
          ...subtopic,
          subSubTopics: subtopic.subSubTopics
            ? [...subtopic.subSubTopics]
            : undefined,
        }))
      : [];
  };

  const getSubSubtopicsForTopic = (
    topicCode: string | number | null | undefined,
    subTopicCode: string,
  ): SubSubTopic[] => {
    const topicKey = resolveTopicKey(topicCode);

    if (!topicKey) return [];

    const topic = ESRS_TOPICS[topicKey];

    if (!topic) return [];

    const subtopic = topic.subtopics.find((sub) => sub.code === subTopicCode);

    return subtopic?.subSubTopics ? [...subtopic.subSubTopics] : [];
  };

  return {
    ESRS_TOPICS,
    TOPIC_OPTIONS,
    getSubtopicsForTopic,
    getSubSubtopicsForTopic,
  };
};
